<template>
  <v-dialog :value="value" max-width="600" @input="onDialogInput">
    <v-card class="specific-datetime-dialog-card">
      <v-card-title class="headline" style="font-size: 1rem">
        {{ cardTitle }}
      </v-card-title>
      <v-card-text>
        <v-layout column class="specific-datetime-dialog-fields mt-2">
          <v-flex xs12 class="specific-datetime-dialog-field">
            <v-layout class="custom-group" column justify-center align-start>
              <div class="label-with-tooltip">
                <span class="placeholder">{{ dateSectionLabel }}</span>
                <AuctionFormInfoTooltip
                  :text="
                    isStart
                      ? 'The exact time when the auction takes place.'
                      : `When the English auction closes. Must be at least ${reverseContinuousSpecificMinGapMinutes} minutes after the auction start.`
                  "
                />
              </div>
              <v-menu
                ref="scheduleDateMenu"
                v-model="syncDateMenuOpen"
                :close-on-content-click="false"
                :nudge-right="50"
                min-width="0px"
                max-width="328"
                transition="scale-transition"
                offset-y
                content-class="specific-schedule-picker-menu"
              >
                <template #activator="{ on: menuOn }">
                  <v-text-field
                    v-on="menuOn"
                    v-bind="dateTextFieldExtra"
                    :value="draftDateDisplay"
                    class="custom-input"
                    placeholder="DD-MM-YYYY"
                    @click.prevent="draftDateDisplay = ''"
                    :id="dateFieldId"
                    append-icon="event"
                    solo
                    @input="draftDateDisplay = $event"
                    @blur="onDateBlur"
                  ></v-text-field>
                </template>
                <v-date-picker
                  :value="draftDateIso"
                  :min="datePickerMin"
                  no-title
                  @input="onDatePickerSelect"
                ></v-date-picker>
              </v-menu>
              <span
                v-if="showValidationErrors && activeErrorKey === 'required_date'"
                class="error-input mt-2"
                >This field is required</span
              >
              <span
                v-if="showValidationErrors && activeErrorKey === 'date_format'"
                class="error-input mt-2"
                >Date has wrong format</span
              >
            </v-layout>
          </v-flex>
          <v-flex xs12 class="specific-datetime-dialog-field specific-datetime-dialog-field--time">
            <v-layout class="custom-group" column justify-center align-start>
              <span class="placeholder">{{ timeSectionLabel }}</span>
              <v-menu
                ref="scheduleTimeMenu"
                v-model="syncTimeMenuOpen"
                :close-on-content-click="false"
                class="datetime-picker"
                :nudge-right="80"
                min-width="0px"
                max-width="328"
                transition="scale-transition"
                offset-y
                content-class="specific-schedule-picker-menu"
              >
                <template #activator="{ on: menuOn }">
                  <v-text-field
                    v-on:keydown.enter.prevent="syncTimeMenuOpen = false"
                    v-on="menuOn"
                    :value="draftTimeValue"
                    ref="timeInput"
                    @input="onTimeFieldInput"
                    @blur="onTimeBlur"
                    class="custom-input"
                    placeholder="XX:XX"
                    :id="timeFieldId"
                    append-icon="access_time"
                    solo
                  ></v-text-field>
                </template>
                <v-time-picker
                  :value="timeValueForPicker"
                  :min="timePickerMin"
                  format="24hr"
                  @input="onTimePickerInput"
                ></v-time-picker>
              </v-menu>
              <span
                v-if="showValidationErrors && activeErrorKey === 'required_time'"
                class="error-input mt-2"
                >This field is required</span
              >
              <span
                v-if="
                  showValidationErrors &&
                  activeErrorKey &&
                  !['required_date', 'date_format', 'required_time'].includes(activeErrorKey)
                "
                class="error-input mt-2"
              >
                {{ activeErrorMessage }}
              </span>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="onCancel">Cancel</v-btn>
        <v-btn text color="primary" @click="onSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue';
import AuctionFormInfoTooltip from '@/components/forms/auctionForm/AuctionFormInfoTooltip.vue';
import { SCHEDULE_SPECIFIC } from './auctionFormConstants';
import {
  parseDate,
  shouldClearTimeField,
  normalizeDateIso,
  formatDate,
  buildScheduleStartMoment,
} from './dateUtils';
import { validateSchedule, getScheduleErrorMessage } from './scheduleValidation';

export default {
  name: 'AuctionFormSpecificDateTimeDialog',
  components: { AuctionFormInfoTooltip },
  props: {
    value: { type: Boolean, required: true },
    /** 'start' | 'end' */
    variant: { type: String, required: true },
    showErrors: { type: Boolean, default: false },
    errorKey: { default: null },
    /** For end variant: parent start schedule (quick offset ms or SCHEDULE_SPECIFIC). */
    referenceStartScheduleValue: { default: null },
    referenceStartDateIso: { default: null },
    referenceStartTime: { default: null },
    tenderPeriodEndDateIso: {},
    reverseContinuousSpecificMinGapMinutes: { type: Number, default: 30 },
    auctionEndDatePickerMin: {},
    auctionEndTimePickerMin: {},
    dateDisplay: {},
    dateIso: {},
    timeValue: {},
    dateMenuOpen: { type: Boolean, default: false },
    timeMenuOpen: { type: Boolean, default: false },
  },
  data() {
    return {
      draftDateDisplay: null,
      draftDateIso: null,
      draftTimeValue: null,
      draftDateMenuOpen: false,
      draftTimeMenuOpen: false,
      saveAttempted: false,
    };
  },
  watch: {
    value(open) {
      if (open) {
        this.initDraftFromProps();
      }
    },
  },
  mounted() {
    if (this.value) {
      this.initDraftFromProps();
    }
  },
  computed: {
    isStart() {
      return this.variant === 'start';
    },
    cardTitle() {
      return this.isStart ? 'Auction start — specific time' : 'Auction end — specific time';
    },
    dateSectionLabel() {
      return this.isStart ? 'Auction Start Date' : 'Auction End Date';
    },
    timeSectionLabel() {
      return this.isStart ? '\u00a0Time' : '\u00a0End time *';
    },
    dateFieldId() {
      return this.isStart ? 'auction-tender-period' : 'auction-end-period';
    },
    timeFieldId() {
      return this.isStart ? 'auc-start-time' : 'auc-end-time';
    },
    datePickerMin() {
      if (this.isStart) return Vue.moment().format('YYYY-MM-DD');
      const start = this.getReferenceStartMoment();
      if (!start || !start.isValid()) return this.auctionEndDatePickerMin;
      return start
        .clone()
        .add(this.reverseContinuousSpecificMinGapMinutes, 'minutes')
        .format('YYYY-MM-DD');
    },
    timePickerMin() {
      if (this.isStart) {
        const todayIso = Vue.moment().format('YYYY-MM-DD');
        const selectedIso = normalizeDateIso(this.draftDateIso);
        if (selectedIso === todayIso) {
          return Vue.moment().add(20, 'minutes').format('HH:mm');
        }
        return undefined;
      }
      const start = this.getReferenceStartMoment();
      if (!start || !start.isValid()) return this.auctionEndTimePickerMin;
      const selectedIso = normalizeDateIso(this.draftDateIso);
      if (!selectedIso) return this.auctionEndTimePickerMin;
      const minEnd = start.clone().add(this.reverseContinuousSpecificMinGapMinutes, 'minutes');
      if (selectedIso === minEnd.format('YYYY-MM-DD')) {
        return minEnd.format('HH:mm');
      }
      return undefined;
    },
    syncDateMenuOpen: {
      get() {
        return this.draftDateMenuOpen;
      },
      set(v) {
        this.draftDateMenuOpen = v;
      },
    },
    syncTimeMenuOpen: {
      get() {
        return this.draftTimeMenuOpen;
      },
      set(v) {
        this.draftTimeMenuOpen = v;
      },
    },
    dateTextFieldExtra() {
      return this.isStart ? { hint: 'DD-MM-YYYY', 'persistent-hint': true } : {};
    },
    /** v-time-picker breaks on null/empty; use a safe display value only in the picker. */
    timeValueForPicker() {
      const t = this.draftTimeValue;
      if (t != null && String(t).includes(':')) return t;
      return '00:00';
    },
    showValidationErrors() {
      return this.showErrors || this.saveAttempted || Boolean(this.errorKey);
    },
    draftErrorKey() {
      return this.evaluateDraftValidation().errorKey;
    },
    activeErrorKey() {
      if (this.saveAttempted) return this.draftErrorKey;
      if (this.showErrors) return this.errorKey;
      return null;
    },
    activeErrorMessage() {
      return getScheduleErrorMessage(this.activeErrorKey, {
        reverseContinuousSpecificMinGapMinutes: this.reverseContinuousSpecificMinGapMinutes,
      });
    },
  },
  methods: {
    evaluateDraftValidation() {
      this.syncDraftDateIsoFromDisplay();
      const validation = validateSchedule({
        isEnglishReverseContinuousType: !this.isStart,
        reverseContinuousSpecificMinGapMinutes: this.reverseContinuousSpecificMinGapMinutes,
        startScheduleValue: this.isStart ? SCHEDULE_SPECIFIC : this.referenceStartScheduleValue,
        startDateDisplay: this.isStart
          ? this.draftDateDisplay
          : formatDate(this.referenceStartDateIso),
        startDateIso: this.isStart
          ? normalizeDateIso(this.draftDateIso)
          : this.referenceStartDateIso,
        startTime: this.isStart ? this.draftTimeValue : this.referenceStartTime,
        endScheduleValue: this.isStart ? null : SCHEDULE_SPECIFIC,
        endDateDisplay: this.isStart ? null : this.draftDateDisplay,
        endDateIso: this.isStart ? null : normalizeDateIso(this.draftDateIso),
        endTime: this.isStart ? null : this.draftTimeValue,
        nowMoment: Vue.moment(),
      });
      return { errorKey: this.isStart ? validation.startErrorKey : validation.endErrorKey };
    },
    initDraftFromProps() {
      this.saveAttempted = false;
      this.draftDateDisplay = this.dateDisplay;
      this.draftDateIso = this.dateIso;
      this.draftTimeValue = this.timeValue;
      this.draftDateMenuOpen = false;
      this.draftTimeMenuOpen = false;
    },
    closeMenusToParent() {
      this.$emit('update:dateMenuOpen', false);
      this.$emit('update:timeMenuOpen', false);
    },
    onDialogInput(open) {
      if (!open) {
        this.saveAttempted = false;
        this.draftDateMenuOpen = false;
        this.draftTimeMenuOpen = false;
        this.closeMenusToParent();
      }
      this.$emit('input', open);
    },
    onCancel() {
      this.saveAttempted = false;
      this.draftDateMenuOpen = false;
      this.draftTimeMenuOpen = false;
      this.closeMenusToParent();
      this.$emit('input', false);
    },
    onSave() {
      if (!this.validateDraft()) {
        this.$emit('save-invalid', {
          variant: this.variant,
          errorKey: this.draftErrorKey,
        });
        return;
      }
      this.saveAttempted = false;
      this.$emit('update:dateDisplay', this.draftDateDisplay);
      this.$emit('update:dateIso', this.draftDateIso);
      this.$emit('update:timeValue', this.draftTimeValue);
      this.draftDateMenuOpen = false;
      this.draftTimeMenuOpen = false;
      this.closeMenusToParent();
      this.$emit('saved', {
        variant: this.variant,
        dateDisplay: this.draftDateDisplay,
        dateIso: this.draftDateIso,
        timeValue: this.draftTimeValue,
      });
      this.$emit('input', false);
    },
    getReferenceStartMoment() {
      return buildScheduleStartMoment(
        this.referenceStartScheduleValue,
        this.referenceStartDateIso,
        this.referenceStartTime
      );
    },
    syncDraftDateIsoFromDisplay() {
      const display = this.draftDateDisplay;
      if (!display || shouldClearTimeField(display)) return;
      if (String(display).includes('-')) {
        const iso = parseDate(display);
        if (iso) this.draftDateIso = iso;
      }
    },
    validateDraft() {
      const { errorKey } = this.evaluateDraftValidation();
      if (errorKey) {
        this.saveAttempted = true;
        return false;
      }
      return true;
    },
    onDateBlur() {
      const display = this.draftDateDisplay;
      if (shouldClearTimeField(display)) {
        this.draftDateDisplay = null;
        this.draftDateIso = null;
        return;
      }
      this.draftDateIso = parseDate(display);
    },
    onDatePickerSelect(iso) {
      const normalized = normalizeDateIso(iso);
      const isoDate = normalized || (typeof iso === 'string' ? iso.slice(0, 10) : '');
      this.draftDateIso = isoDate;
      const display = formatDate(typeof isoDate === 'string' ? isoDate.slice(0, 10) : isoDate);
      if (display) {
        this.draftDateDisplay = display;
      }
      this.draftDateMenuOpen = false;
      this.$nextTick(() => {
        this.draftTimeMenuOpen = true;
      });
    },

    onTimeBlur() {
      if (shouldClearTimeField(this.draftTimeValue)) {
        this.draftTimeValue = null;
      }
    },
    onTimeFieldInput(val) {
      const v = typeof val === 'string' ? val : val && val.target != null ? val.target.value : '';
      this.draftTimeValue = v;
    },
    onTimePickerInput(val) {
      this.draftTimeValue = val;
      this.$nextTick(() => {
        const menu = this.$refs.scheduleTimeMenu;
        if (menu && menu.save) {
          menu.save(val);
        }
      });
    },
  },
};
</script>

<style scoped>
.specific-datetime-dialog-card {
  padding: 20px 24px 20px;
}

.specific-datetime-dialog-card ::v-deep .v-card__title {
  padding: 0 0 12px;
}

.specific-datetime-dialog-card ::v-deep .v-card__text {
  padding: 0;
}

.specific-datetime-dialog-card ::v-deep .v-card__actions {
  padding: 16px 0 0;
}

.specific-datetime-dialog-fields {
  width: 100%;
}

.specific-datetime-dialog-fields ::v-deep .flex {
  max-width: 100%;
}

.specific-datetime-dialog-field {
  width: 100%;
}

.label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.specific-datetime-dialog-field--time {
  margin-top: 16px;
}
</style>

<!-- Teleported v-menu content: scoped attrs do not apply -->
<style>
.specific-schedule-picker-menu {
  min-width: 0 !important;
}

.specific-schedule-picker-menu .v-picker {
  max-width: 100%;
}
</style>
